import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
} from '@mui/material';
import { type FC, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Text, MobileButton, MobileInput } from '@/components/ui';
import { Header, Layout } from '@/components/layout';
import { useDialogHelpers } from '@/components/feedback';
import { EditableQty } from './MaterialsAndPricingTable/components/EditableQty';
interface Props {
  closeMaterials: () => void;
  localSiteMaterials: QuantityAndPrice[];
  saveMaterials: (localSiteMaterials: QuantityAndPrice[]) => void;
  availableMaterials: MaterialLists[];
}

export interface QuantityAndPrice {
  quantity: number;
  id: number;
  materialId: number;
  unitCost: number;
  materialCode: string;
  shortDescription: string;
  unitType: string;
}

interface MaterialLists {
  id: number;
  materialCode: string;
  shortDescription?: string;
  unitType?: string;
  unitCost: number;
  materialFormatId: number;
}

export const AddMaterialsModal: FC<Props> = ({
  closeMaterials,
  saveMaterials,
  localSiteMaterials,
  availableMaterials,
}) => {
  const { t } = useTranslation();
  const { confirm } = useDialogHelpers();
  const [searchText, setSearchText] = useState<string>('');
  const [newSiteMaterials, setNewSiteMaterials] = useState<QuantityAndPrice[]>(
    []
  );
  const [changedList, setChangedList] = useState<boolean>(false);

  const onChangeSearch = (value: string) => {
    setSearchText(value);
  };

  const searchMaterials = (
    filteredItems: MaterialLists[],
    searchText: string
  ) => {
    const searched = filteredItems.filter(c => {
      const values = Object.values(c);
      return values.some(v =>
        v
          ?.toString()
          .toLocaleLowerCase()
          .includes(searchText.toLocaleLowerCase().trim())
      );
    });
    return searched;
  };

  useEffect(() => {
    setNewSiteMaterials(localSiteMaterials);
  }, [localSiteMaterials]);

  const filteredMaterials: MaterialLists[] = useMemo(() => {
    const searchedItems = searchMaterials(availableMaterials, searchText);
    return searchedItems.sort((a, b) =>
      (a.shortDescription || '').localeCompare(b.shortDescription || '')
    );
  }, [searchText, availableMaterials]);

  const changeCheckboxStatus = (checked: boolean, itemID: number) => {
    setChangedList(true);
    const newLocalSiteMaterials: QuantityAndPrice[] = [...newSiteMaterials];

    if (!checked) {
      const index = newLocalSiteMaterials.findIndex(
        item => item.materialId === itemID
      );
      newLocalSiteMaterials.splice(index, 1);
      setNewSiteMaterials(newLocalSiteMaterials);
    } else {
      const material = availableMaterials.find(item => item.id === itemID);
      if (material) {
        const newMaterial = {
          quantity: 1,
          materialCode: material.materialCode,
          id: material.id,
          materialId: material.id,
          unitType: material.unitType || '',
          shortDescription: material.shortDescription || '',
          unitCost: material.unitCost,
        };
        newLocalSiteMaterials.push(newMaterial);
        setNewSiteMaterials(newLocalSiteMaterials);
      }
    }
  };

  const handleBackButton = async () => {
    if (changedList) {
      const confirmed = await confirm({
        title: t('confirm_exit_title'),
        message: t('leave_without_saving_changes'),
        confirmText: t('yes_confirm_modal'),
        cancelText: t('cancel_button'),
      });

      if (confirmed) {
        closeMaterials();
      }
    } else {
      closeMaterials();
    }
  };

  const saveSiteMaterials = () => {
    saveMaterials(newSiteMaterials);
    closeMaterials();
  };

  const updateMaterialQuantity = (materialId: number, newQuantity: number) => {
    setChangedList(true);
    const updatedMaterials = [...newSiteMaterials];

    if (newQuantity <= 0) {
      // Remove material if quantity is 0 or negative
      const index = updatedMaterials.findIndex(
        item => item.materialId === materialId
      );
      if (index !== -1) {
        updatedMaterials.splice(index, 1);
      }
    } else {
      // Find existing material or add new one
      const existingIndex = updatedMaterials.findIndex(
        item => item.materialId === materialId
      );

      if (existingIndex !== -1) {
        // Update existing material quantity
        updatedMaterials[existingIndex].quantity = newQuantity;
      } else {
        // Add new material with specified quantity
        const material = availableMaterials.find(
          item => item.id === materialId
        );
        if (material) {
          const newMaterial = {
            quantity: newQuantity,
            materialCode: material.materialCode,
            id: material.id,
            materialId: material.id,
            unitType: material.unitType || '',
            shortDescription: material.shortDescription || '',
            unitCost: material.unitCost,
          };
          updatedMaterials.push(newMaterial);
        }
      }
    }

    setNewSiteMaterials(updatedMaterials);
  };

  const handleQuantityIncrement = (materialId: number) => {
    const currentMaterial = newSiteMaterials.find(
      item => item.materialId === materialId
    );
    const currentQuantity = currentMaterial?.quantity || 0;
    updateMaterialQuantity(materialId, currentQuantity + 1);
  };

  const handleQuantityDecrement = (materialId: number) => {
    const currentMaterial = newSiteMaterials.find(
      item => item.materialId === materialId
    );
    const currentQuantity = currentMaterial?.quantity || 0;
    updateMaterialQuantity(materialId, Math.max(0, currentQuantity - 1));
  };

  const handleQuantityChange = (materialId: number, newQuantity: number) => {
    updateMaterialQuantity(materialId, Math.max(0, newQuantity));
  };

  // Simple checkbox component
  const SimpleCheckbox = ({
    checked,
    onChange,
  }: {
    checked: boolean;
    onChange: (checked: boolean) => void;
  }) => (
    <input
      type='checkbox'
      checked={checked}
      onChange={e => onChange(e.target.checked)}
      style={{ width: '1.125rem', height: '1.125rem' }}
    />
  );

  return (
    <Box
      sx={{
        position: 'fixed',
        height: ['100vh', '100dvh', '100svh'],
        width: '100%',
        top: '0',
        left: '0',
        backgroundColor: 'white',
        zIndex: '500',
      }}
    >
      <Layout>
        <Header title={t('add_materials_title')} onBack={handleBackButton} />
        <Box sx={{ padding: '0' }}>
          <MobileInput
            label={`${t('materials_and_pricing_table')}`}
            name='search'
            error={false}
            helperText=''
            onChange={e => onChangeSearch(e.target.value)}
            value={searchText}
            placeholder={
              t('materials_and_pricing_table_placeholder') ||
              'Search materials...'
            }
            compact
          />

          <Box
            sx={{
              height: 'calc(100vh - 20rem)',
              overflowX: 'hidden',
              overflowY: 'auto',
              mt: 2,
            }}
          >
            <Box>
              <Table
                sx={{
                  border: '1px solid rgba(224, 224, 224, 1)',
                  tableLayout: 'fixed',
                  width: '100%',
                }}
              >
                <TableHead>
                  <TableRow sx={{ background: '#FAFAFA' }}>
                    <TableCell
                      sx={{
                        height: '3.75rem',
                        padding: { xs: '0.25rem', sm: '0.5rem' },
                        width: { xs: '1rem', sm: '3.125rem' },
                        minWidth: '1rem',
                      }}
                    >
                      <Text
                        sx={{
                          color: 'secondary.dark',
                          fontSize: { xs: '0.625rem', sm: '0.875rem' },
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                        }}
                      >
                        Select
                      </Text>
                    </TableCell>
                    <TableCell
                      sx={{
                        height: '3.75rem',
                        padding: { xs: '0.25rem', sm: '0.75rem' },
                        width: { xs: '1.25rem', sm: 'auto' },
                        minWidth: '1.25rem',
                      }}
                    >
                      <Text
                        sx={{
                          color: 'secondary.dark',
                          fontSize: { xs: '0.625rem', sm: '0.875rem' },
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                        }}
                      >
                        {t('code_material_table')}
                      </Text>
                    </TableCell>
                    <TableCell
                      sx={{
                        height: '3.75rem',
                        padding: { xs: '0.25rem', sm: '0.75rem' },
                        width: { xs: '1.25rem', sm: 'auto' },
                        minWidth: '1.25rem',
                      }}
                    >
                      <Text
                        sx={{
                          color: 'secondary.dark',
                          fontSize: { xs: '0.625rem', sm: '0.875rem' },
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                        }}
                      >
                        {t('description_material_table')}
                      </Text>
                    </TableCell>
                    <TableCell
                      sx={{
                        height: '3.75rem',
                        padding: { xs: '0.25rem', sm: '0.75rem' },
                        width: { xs: '1.25rem', sm: 'auto' },
                        minWidth: '1.25rem',
                      }}
                    >
                      <Text
                        sx={{
                          color: 'secondary.dark',
                          fontSize: { xs: '0.625rem', sm: '0.875rem' },
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                        }}
                      >
                        {t('unit_material_table')}
                      </Text>
                    </TableCell>
                    <TableCell
                      sx={{
                        height: '3.75rem',
                        padding: { xs: '0.25rem', sm: '0.75rem' },
                        width: { xs: '2.5rem', sm: 'auto' },
                        minWidth: '2.5rem',
                      }}
                    >
                      <Text
                        sx={{
                          color: 'secondary.dark',
                          fontSize: { xs: '0.625rem', sm: '0.875rem' },
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                        }}
                      >
                        {t('price_material_table')}
                      </Text>
                    </TableCell>
                    <TableCell
                      sx={{
                        height: '3.75rem',
                        padding: { xs: '0.25rem', sm: '0.75rem' },
                        width: { xs: '3.5rem', sm: 'auto' },
                        minWidth: '3.5rem',
                      }}
                    >
                      <Text
                        sx={{
                          color: 'secondary.dark',
                          fontSize: { xs: '0.625rem', sm: '0.875rem' },
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                        }}
                      ></Text>
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredMaterials.map((item, index) => (
                    <TableRow
                      key={`key-${item.id}-${index}`}
                      sx={{ background: index % 2 ? '#FAFAFA' : 'white' }}
                    >
                      <TableCell
                        sx={{
                          height: '3.75rem',
                          padding: { xs: '0rem', sm: '0.5rem' },
                          textAlign: 'center',
                          width: { xs: '1.75rem', sm: '3.125rem' },
                          minWidth: '1.75rem',
                        }}
                      >
                        <SimpleCheckbox
                          checked={
                            !!newSiteMaterials.find(
                              el => el.materialId === item.id
                            )
                          }
                          onChange={checked =>
                            changeCheckboxStatus(checked, item.id)
                          }
                        />
                      </TableCell>
                      <TableCell
                        sx={{
                          height: '3.75rem',
                          padding: { xs: '0.25rem', sm: '0.75rem' },
                          width: { xs: '6.25rem', sm: 'auto' },
                          minWidth: '6.25rem',
                        }}
                      >
                        <Text
                          sx={{
                            color: 'secondary.dark',
                            fontSize: { xs: '0.625rem', sm: '0.875rem' },
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            display: 'block',
                            maxWidth: '100%',
                          }}
                        >
                          {item.materialCode}
                        </Text>
                      </TableCell>
                      <TableCell
                        sx={{
                          height: '3.75rem',
                          padding: { xs: '0.25rem', sm: '0.75rem' },
                          width: { xs: '7.5rem', sm: 'auto' },
                          minWidth: '7.5rem',
                        }}
                      >
                        <Text
                          sx={{
                            color: 'secondary.dark',
                            fontSize: { xs: '0.625rem', sm: '0.875rem' },
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            display: 'block',
                            maxWidth: '100%',
                          }}
                        >
                          {item.shortDescription || ''}
                        </Text>
                      </TableCell>
                      <TableCell
                        sx={{
                          height: '3.75rem',
                          padding: { xs: '0.25rem', sm: '0.75rem' },
                          width: { xs: '3.75rem', sm: 'auto' },
                          minWidth: '3.75rem',
                        }}
                      >
                        <Text
                          sx={{
                            color: 'secondary.dark',
                            fontSize: { xs: '0.625rem', sm: '0.875rem' },
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            display: 'block',
                            maxWidth: '100%',
                          }}
                        >
                          {item.unitType || ''}
                        </Text>
                      </TableCell>
                      <TableCell
                        sx={{
                          height: '3.75rem',
                          padding: { xs: '0.25rem', sm: '0.75rem' },
                          width: { xs: '5rem', sm: 'auto' },
                          minWidth: '5rem',
                        }}
                      >
                        <Text
                          sx={{
                            color: 'secondary.dark',
                            fontSize: { xs: '0.625rem', sm: '0.875rem' },
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            display: 'block',
                            maxWidth: '100%',
                          }}
                        >
                          ₪{item.unitCost.toFixed(2)}
                        </Text>
                      </TableCell>
                      <TableCell
                        sx={{
                          height: '3.75rem',
                          padding: { xs: '0.25rem', sm: '0.75rem' },
                          width: { xs: '7.5rem', sm: 'auto' },
                          minWidth: '7.5rem',
                        }}
                      >
                        <EditableQty
                          value={
                            newSiteMaterials.find(
                              el => el.materialId === item.id
                            )?.quantity || 0
                          }
                          onIncrement={() => handleQuantityIncrement(item.id)}
                          onDecrement={() => handleQuantityDecrement(item.id)}
                          onChange={newQuantity =>
                            handleQuantityChange(item.id, newQuantity)
                          }
                        />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Box>

            {filteredMaterials.length === 0 && (
              <Box sx={{ textAlign: 'center', py: 4 }}>
                <Text sx={{ color: 'grey.600' }}>
                  {searchText
                    ? 'No materials found matching your search'
                    : 'No materials available'}
                </Text>
              </Box>
            )}
          </Box>

          <MobileButton btnColor='#1C5B9C' onClick={saveSiteMaterials}>
            <Text>{t('save_list_material')}</Text>
          </MobileButton>
        </Box>
      </Layout>
    </Box>
  );
};
